import moment from 'moment';

/**
 * Enum for period types
 */
const ProjectMilestonePeriodType = {
    Years: 'years',
    Quarters: 'quarters',
    Months: 'months',
    Days: 'days',
};

/**
 * @param {moment.Moment|null} startDate
 * @param {moment.Moment|null} endDate
 * @param {string} period - One of ProjectMilestonePeriodType values
 * @returns {object}
 */
function getTimeIntervals(startDate, endDate, period) {
    const result = {};

    if (!startDate || !endDate) {
        return result;
    }

    const validPeriods = Object.values(ProjectMilestonePeriodType);

    if (validPeriods.includes(period)) {
        result.years = {};
        result.years_map = [];
        result.quarters_map = [];
        result.months_map = [];
        result.days_map = [];
        result.years_count = 0;
        result.quarters_count = 0;
        result.months_count = 0;
        result.days_count = 0;

        // Iterate through years
        const yearCursor = startDate.clone().startOf('year');
        const endYear = endDate.clone().endOf('year');

        while (yearCursor.isSameOrBefore(endYear, 'year')) {
            const year = yearCursor.year();
            result.years_map.push(year);
            result.years_count++;

            const yearStart = yearCursor.clone().startOf('year');
            const yearEnd = yearCursor.clone().endOf('year');

            const data = {
                label: year,
                start: yearStart,
                end: yearEnd,
            };

            data.quarters_count = 0;
            data.months_count = 0;
            data.days_count = 0;

            if (period === ProjectMilestonePeriodType.Quarters) {
                data.quarters = {};

                const quarterStart = moment.max(yearStart, startDate.clone().startOf('quarter'));
                const quarterEnd = moment.min(yearEnd, endDate.clone().endOf('quarter'));
                const quarterCursor = quarterStart.clone();

                while (quarterCursor.isSameOrBefore(quarterEnd)) {
                    result.quarters_count++;
                    data.quarters_count++;
                    const qStart = quarterCursor.clone().startOf('quarter');
                    const qEnd = quarterCursor.clone().endOf('quarter');
                    const quarterKey = `${qStart.month() + 1}-${qEnd.month() + 1}`;
                    const quarterMapKey = `${qStart.format('MM-YYYY')}::${qEnd.format('MM-YYYY')}`;
                    result.quarters_map.push(quarterMapKey);

                    data.quarters[quarterKey] = {
                        key: quarterMapKey,
                        label: `${qStart.format('MMM')} - ${qEnd.format('MMM')}`,
                        start: qStart,
                        end: qEnd,
                    };

                    quarterCursor.add(1, 'quarter');
                }
            }

            if (period === ProjectMilestonePeriodType.Months || period === ProjectMilestonePeriodType.Days) {
                data.months = {};

                const monthStart = moment.max(yearStart, startDate.clone().startOf('month'));
                const monthEnd = moment.min(yearEnd, endDate.clone().endOf('month'));
                const monthCursor = monthStart.clone();

                while (monthCursor.isSameOrBefore(monthEnd, 'month')) {
                    const monthMapKey = monthCursor.format('MM-YYYY');
                    result.months_map.push(monthMapKey);
                    result.months_count++;
                    data.months_count++;

                    const mStart = monthCursor.clone().startOf('month');
                    const mEnd = monthCursor.clone().endOf('month');

                    const monthData = {
                        key: monthMapKey,
                        label: monthCursor.format('MMMM'),
                        start: mStart,
                        end: mEnd,
                    };

                    if (period === ProjectMilestonePeriodType.Days) {
                        monthData.days = {};

                        const dayStart = moment.max(mStart, startDate.clone().startOf('day'));
                        const dayEnd = moment.min(mEnd, endDate.clone().endOf('day'));
                        const dayCursor = dayStart.clone();

                        while (dayCursor.isSameOrBefore(dayEnd, 'day')) {
                            const dayMapKey = dayCursor.format('DD-MM-YYYY');
                            result.days_map.push(dayMapKey);
                            result.days_count++;
                            data.days_count++;

                            monthData.days[dayCursor.date()] = {
                                key: dayMapKey,
                                label: dayCursor.format('ddd Do'),
                                start: dayCursor.clone().startOf('day'),
                                end: dayCursor.clone().endOf('day'),
                            };

                            dayCursor.add(1, 'day');
                        }
                    }

                    data.months[monthCursor.month() + 1] = monthData;
                    monthCursor.add(1, 'month');
                }
            }

            result.years[year] = data;
            yearCursor.add(1, 'year');
        }
    }

    return result;
}

export { getTimeIntervals, ProjectMilestonePeriodType };
